import { useState } from 'react'
import { AdminLayout } from '@/layouts/admin-layout'
import { AppHeader } from '@/components/app-header'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Link, router } from '@inertiajs/react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog'
import { XIcon, AlertTriangleIcon, LightbulbIcon } from 'lucide-react'
import { StarRating } from '@/components/ui/star-rating'

interface Review {
  id: number
  score: number
  feedback: string | null
  improvement_suggestion: string | null
  dev_instance_id: number
  created_at: string
}

interface Manual {
  id: number
  name: string
  description: string
  domain: string
  platforms: string[]
  tags: string[]
  keywords: string[]
  behavior: string | null
  documentation: string
  usage_count: number
  average_rating: number
  reviews_count: number
  has_suggestions: boolean
  needs_attention: boolean
  recent_reviews: Review[]
  active: boolean
  rejection_reason: string | null
  reviewed_by: string | null
  reviewed_at: string | null
  message_id: number | null
  dev_instance_id: number | null
  created_at: string
}

interface Props {
  manual: Manual
}

function Show({ manual }: Props) {
  const [isRejecting, setIsRejecting] = useState(false)
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false)
  const [rejectionReason, setRejectionReason] = useState('')

  const basePath = '/admin/collective_knowledge/manuals'

  const handleReject = () => {
    setIsRejecting(true)
    router.post(`${basePath}/${manual.id}/reject`, {
      reason: rejectionReason
    }, {
      onFinish: () => {
        setIsRejecting(false)
        setRejectDialogOpen(false)
        setRejectionReason('')
      }
    })
  }

  return (
    <>
      <AppHeader breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: 'Manuals', href: basePath },
        { label: manual.name }
      ]} />

      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
            <div className="px-4 lg:px-6">
              {/* Page Header */}
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-3">
                    <h1 className="text-3xl font-bold text-foreground">{manual.name}</h1>
                    <Badge variant={manual.active ? 'success' : 'destructive'}>
                      {manual.active ? 'Active' : 'Rejected'}
                    </Badge>
                    <Badge variant="secondary">{manual.domain}</Badge>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">{manual.description}</p>
                </div>
                <Button variant="outline" asChild>
                  <Link href={basePath}>Back to list</Link>
                </Button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                  {manual.behavior && (
                    <Card className="shadow-none">
                      <CardHeader>
                        <CardTitle>Behavior</CardTitle>
                        <CardDescription>Decision flow and usage patterns</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <pre className="p-4 bg-muted rounded-md overflow-x-auto text-sm whitespace-pre-wrap">
                          {manual.behavior}
                        </pre>
                      </CardContent>
                    </Card>
                  )}

                  <Card className="shadow-none">
                    <CardHeader>
                      <CardTitle>Documentation</CardTitle>
                      <CardDescription>Implementation details and instructions</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <pre className="p-4 bg-muted rounded-md overflow-x-auto text-sm whitespace-pre-wrap">
                        {manual.documentation}
                      </pre>
                    </CardContent>
                  </Card>

                  {manual.rejection_reason && (
                    <Card className="shadow-none border-destructive">
                      <CardHeader>
                        <CardTitle className="text-destructive">Rejection Reason</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm">{manual.rejection_reason}</p>
                        {manual.reviewed_by && (
                          <p className="text-xs text-muted-foreground mt-2">
                            By {manual.reviewed_by} on {manual.reviewed_at ? new Date(manual.reviewed_at).toLocaleDateString() : 'N/A'}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                  {/* Actions */}
                  {manual.active && (
                    <Card className="shadow-none">
                      <CardHeader>
                        <CardTitle>Actions</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
                          <DialogTrigger asChild>
                            <Button variant="outline" className="w-full justify-start text-red-600">
                              <XIcon className="mr-2 h-4 w-4" />
                              Reject Manual
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Reject Manual</DialogTitle>
                              <DialogDescription>
                                This will deactivate the manual and it will no longer be returned in searches.
                              </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                              <div className="space-y-2">
                                <Label htmlFor="reason">Reason</Label>
                                <Textarea
                                  id="reason"
                                  value={rejectionReason}
                                  onChange={(e) => setRejectionReason(e.target.value)}
                                  placeholder="Why is this manual being rejected?"
                                  rows={3}
                                />
                              </div>
                            </div>
                            <DialogFooter>
                              <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
                                Cancel
                              </Button>
                              <Button
                                variant="destructive"
                                onClick={handleReject}
                                disabled={isRejecting || !rejectionReason.trim()}
                              >
                                {isRejecting ? 'Rejecting...' : 'Reject'}
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                      </CardContent>
                    </Card>
                  )}

                  {/* Agent Reviews */}
                  <Card className="shadow-none">
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span>Agent Reviews</span>
                        {manual.reviews_count > 0 && (
                          <span className="text-sm font-normal text-muted-foreground">({manual.reviews_count})</span>
                        )}
                      </CardTitle>
                      {manual.reviews_count > 0 && (
                        <div className="flex items-center gap-2">
                          <StarRating value={manual.average_rating} size="md" />
                          <span className="text-sm font-medium">{manual.average_rating.toFixed(1)}</span>
                        </div>
                      )}
                    </CardHeader>
                    <CardContent>
                      {manual.needs_attention && (
                        <div className="mb-3 p-2 bg-amber-50 dark:bg-amber-950/20 rounded-md flex items-start gap-2">
                          <AlertTriangleIcon className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                          <span className="text-xs text-amber-700 dark:text-amber-400">
                            Low rating or pending improvement suggestions. Consider reviewing and updating this manual.
                          </span>
                        </div>
                      )}
                      {manual.recent_reviews && manual.recent_reviews.length > 0 ? (
                        <div className="space-y-3">
                          {manual.recent_reviews.map((review) => (
                            <div key={review.id} className="border-b border-border last:border-0 pb-2 last:pb-0">
                              <div className="flex items-center justify-between mb-1">
                                <StarRating value={review.score} />
                                <span className="text-[10px] text-muted-foreground">
                                  {new Date(review.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </span>
                              </div>
                              {review.feedback && (
                                <p className="text-xs text-muted-foreground">{review.feedback}</p>
                              )}
                              {review.improvement_suggestion && (
                                <div className="mt-1 p-1.5 bg-blue-50 dark:bg-blue-950/20 rounded flex items-start gap-1.5">
                                  <LightbulbIcon className="h-3 w-3 text-blue-500 mt-0.5 shrink-0" />
                                  <span className="text-xs text-blue-700 dark:text-blue-400">{review.improvement_suggestion}</span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground">No reviews yet</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Info */}
                  <Card className="shadow-none">
                    <CardHeader>
                      <CardTitle>Info</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <dl className="space-y-3 text-sm">
                        <div>
                          <dt className="text-muted-foreground">Domain</dt>
                          <dd><Badge variant="secondary">{manual.domain}</Badge></dd>
                        </div>
                        {manual.platforms.length > 0 && (
                          <div>
                            <dt className="text-muted-foreground">Platforms</dt>
                            <dd className="flex gap-1 mt-1">
                              {manual.platforms.map((p) => (
                                <Badge key={p} variant="secondary" className="text-xs">{p}</Badge>
                              ))}
                            </dd>
                          </div>
                        )}
                        {manual.tags.length > 0 && (
                          <div>
                            <dt className="text-muted-foreground">Tags</dt>
                            <dd className="flex flex-wrap gap-1 mt-1">
                              {manual.tags.map((tag) => (
                                <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                              ))}
                            </dd>
                          </div>
                        )}
                        {manual.keywords.length > 0 && (
                          <div>
                            <dt className="text-muted-foreground">Keywords</dt>
                            <dd className="flex flex-wrap gap-1 mt-1">
                              {manual.keywords.map((kw) => (
                                <Badge key={kw} variant="secondary" className="text-[10px] px-1 py-0">{kw}</Badge>
                              ))}
                            </dd>
                          </div>
                        )}
                        <div>
                          <dt className="text-muted-foreground">Usage Count</dt>
                          <dd className="font-medium">{manual.usage_count}</dd>
                        </div>
                        <div>
                          <dt className="text-muted-foreground">Created</dt>
                          <dd>{new Date(manual.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</dd>
                        </div>
                        {manual.message_id && (
                          <div>
                            <dt className="text-muted-foreground">Source Message</dt>
                            <dd>#{manual.message_id}</dd>
                          </div>
                        )}
                        {manual.dev_instance_id && (
                          <div>
                            <dt className="text-muted-foreground">Dev Instance</dt>
                            <dd>#{manual.dev_instance_id}</dd>
                          </div>
                        )}
                      </dl>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

Show.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Show
